import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  Lightbulb, TrendingUp, DollarSign, BarChart3, 
  ChevronDown, ChevronUp, Sparkles, Target, RefreshCw
} from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import { COMPANY } from '../config/company';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const SmartPricingSuggestions = ({ projectType, county, squareFootage, onApplyPrice }) => {
  const { language } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(true);
  const [error, setError] = useState(null);

  const fetchPricing = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${BACKEND_URL}/api/ai-assistant/smart-pricing`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          project_type: projectType,
          county: county,
          square_footage: squareFootage
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        setData(result);
      } else {
        setError('Failed to fetch pricing data');
      }
    } catch (err) {
      console.error('Error fetching smart pricing:', err);
      setError('Connection error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (projectType || county) {
      fetchPricing();
    }
  }, [projectType, county, squareFootage]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: 'USD', 
      maximumFractionDigits: 0 
    }).format(amount || 0);
  };

  const formatPercent = (value) => {
    return value ? `${value.toFixed(0)}%` : 'N/A';
  };

  if (!projectType && !county) {
    return null; // Don't show if no criteria
  }

  return (
    <Card className="border-2 border-amber-200 bg-gradient-to-r from-amber-50 to-yellow-50 mb-4">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-semibold flex items-center gap-2" style={{ color: COMPANY.primaryColor }}>
            <Lightbulb className="h-4 w-4" />
            {language === 'es' ? 'Sugerencias de Precio AI' : 'AI Smart Pricing'}
            <Badge variant="outline" className="text-[10px] bg-amber-100 border-amber-300">
              {language === 'es' ? 'Basado en historial' : 'Based on history'}
            </Badge>
          </CardTitle>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={fetchPricing}
              disabled={loading}
              className="h-6 px-2"
            >
              <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setExpanded(!expanded)}
              className="h-6 px-2"
            >
              {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      </CardHeader>
      
      {expanded && (
        <CardContent className="pt-0">
          {loading ? (
            <div className="flex items-center justify-center py-4">
              <RefreshCw className="h-5 w-5 animate-spin text-amber-500" />
              <span className="ml-2 text-sm text-gray-500">
                {language === 'es' ? 'Analizando historial...' : 'Analyzing history...'}
              </span>
            </div>
          ) : error ? (
            <div className="text-center py-4 text-red-500 text-sm">{error}</div>
          ) : !data?.has_data ? (
            <div className="text-center py-4 text-gray-500 text-sm">
              <Sparkles className="h-6 w-6 mx-auto mb-2 text-gray-300" />
              {language === 'es' 
                ? 'Crea más presupuestos para obtener sugerencias' 
                : 'Create more estimates to get suggestions'}
            </div>
          ) : (
            <div className="space-y-3">
              {/* Criteria Badge */}
              <div className="flex flex-wrap gap-1 text-xs">
                {data.criteria?.project_type && (
                  <Badge variant="outline" className="bg-white">
                    {data.criteria.project_type}
                  </Badge>
                )}
                {data.criteria?.county && (
                  <Badge variant="outline" className="bg-white">
                    {data.criteria.county}
                  </Badge>
                )}
                <Badge variant="secondary" className="text-[10px]">
                  {data.sample_size} {language === 'es' ? 'presupuestos' : 'estimates'}
                </Badge>
              </div>

              {/* Main Price Suggestion */}
              <div className="grid grid-cols-3 gap-2">
                <div className="bg-white rounded-lg p-2 text-center border">
                  <div className="text-[10px] text-gray-500 uppercase">
                    {language === 'es' ? 'Promedio' : 'Average'}
                  </div>
                  <div className="text-lg font-bold text-green-600">
                    {formatCurrency(data.suggestions?.average_price)}
                  </div>
                </div>
                <div className="bg-white rounded-lg p-2 text-center border">
                  <div className="text-[10px] text-gray-500 uppercase">
                    {language === 'es' ? 'Rango' : 'Range'}
                  </div>
                  <div className="text-xs font-medium text-gray-700">
                    {formatCurrency(data.suggestions?.price_range?.min)} - {formatCurrency(data.suggestions?.price_range?.max)}
                  </div>
                </div>
                <div className="bg-white rounded-lg p-2 text-center border">
                  <div className="text-[10px] text-gray-500 uppercase">
                    {language === 'es' ? 'Tasa Ganada' : 'Win Rate'}
                  </div>
                  <div className={`text-lg font-bold ${data.suggestions?.win_rate >= 60 ? 'text-green-600' : data.suggestions?.win_rate >= 40 ? 'text-yellow-600' : 'text-red-600'}`}>
                    {formatPercent(data.suggestions?.win_rate)}
                  </div>
                </div>
              </div>

              {/* Per Sq Ft if available */}
              {data.suggestions?.price_per_sq_ft && (
                <div className="bg-blue-50 rounded-lg p-2 border border-blue-200">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-blue-700">
                      <BarChart3 className="h-3 w-3 inline mr-1" />
                      {language === 'es' ? 'Precio por pie²' : 'Price per sq ft'}
                    </span>
                    <span className="font-bold text-blue-700">
                      ${data.suggestions.price_per_sq_ft.toFixed(2)}/sq ft
                    </span>
                  </div>
                  {data.suggestions?.suggested_price && squareFootage && (
                    <div className="mt-1 flex items-center justify-between text-xs">
                      <span className="text-blue-600">
                        {squareFootage} sq ft × ${data.suggestions.price_per_sq_ft.toFixed(2)}
                      </span>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-6 px-2 text-xs border-blue-300 text-blue-700 hover:bg-blue-100"
                        onClick={() => onApplyPrice && onApplyPrice(data.suggestions.suggested_price)}
                      >
                        <Target className="h-3 w-3 mr-1" />
                        {formatCurrency(data.suggestions.suggested_price)}
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {/* Win Rate by Price Bracket */}
              {data.suggestions?.price_brackets?.length > 0 && (
                <div className="space-y-1">
                  <div className="text-xs font-medium text-gray-600 flex items-center gap-1">
                    <TrendingUp className="h-3 w-3" />
                    {language === 'es' ? 'Tasa de éxito por rango' : 'Win rate by price range'}
                  </div>
                  <div className="grid grid-cols-3 gap-1">
                    {data.suggestions.price_brackets.map((bracket, idx) => (
                      <div 
                        key={idx}
                        className={`text-center p-1.5 rounded text-xs border ${
                          bracket.win_rate && bracket.win_rate >= 60 
                            ? 'bg-green-50 border-green-200' 
                            : bracket.win_rate && bracket.win_rate >= 40 
                            ? 'bg-yellow-50 border-yellow-200' 
                            : 'bg-gray-50 border-gray-200'
                        }`}
                      >
                        <div className="text-[9px] text-gray-500 uppercase">{bracket.bracket}</div>
                        <div className="font-medium">
                          {formatCurrency(bracket.range_min)}-{formatCurrency(bracket.range_max)}
                        </div>
                        <div className={`font-bold ${bracket.win_rate >= 60 ? 'text-green-600' : bracket.win_rate >= 40 ? 'text-yellow-600' : 'text-gray-500'}`}>
                          {formatPercent(bracket.win_rate)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Common Line Items */}
              {data.suggestions?.common_line_items?.length > 0 && (
                <div className="space-y-1">
                  <div className="text-xs font-medium text-gray-600 flex items-center gap-1">
                    <DollarSign className="h-3 w-3" />
                    {language === 'es' ? 'Items frecuentes' : 'Common line items'}
                  </div>
                  <div className="bg-white rounded border p-2 max-h-32 overflow-y-auto">
                    <div className="space-y-1">
                      {data.suggestions.common_line_items.slice(0, 5).map((item, idx) => (
                        <div key={idx} className="flex items-center justify-between text-xs">
                          <span className="text-gray-600 truncate flex-1">{item.description}</span>
                          <span className="text-gray-400 text-[10px] mx-2">{item.usage_pct}%</span>
                          <span className="font-medium text-gray-700">{formatCurrency(item.avg_amount)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Best Price Bracket Insight */}
              {data.insights?.best_bracket && data.insights.best_bracket.win_rate && (
                <div className="bg-green-50 rounded-lg p-2 border border-green-200 text-xs">
                  <div className="flex items-start gap-2">
                    <Sparkles className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <span className="font-medium text-green-700">
                        {language === 'es' ? 'Mejor rango de precio: ' : 'Best price range: '}
                      </span>
                      <span className="text-green-600">
                        {formatCurrency(data.insights.best_bracket.range_min)} - {formatCurrency(data.insights.best_bracket.range_max)}
                      </span>
                      <span className="text-green-700">
                        {' '}({formatPercent(data.insights.best_bracket.win_rate)} {language === 'es' ? 'tasa de éxito' : 'win rate'})
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
};

export default SmartPricingSuggestions;
